import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useState } from 'react';

import { discoverProductAreas } from '@/api/aiVisibility';
import PlusIcon from '@/assets/svg/bx-plus.svg?react';
import { Button } from '@/components/buttons';
import { ButtonGroup } from '@/components/buttons/ButtonGroup';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { FieldError } from '@/components/Form/fields';
import { TextInput } from '@/components/Form/inputs';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/utils';

import { submitOnEnter } from './submitOnEnter';
import ToggleBadge from './ToggleBadge';
import { MAX_PRODUCT_AREAS, MIN_PRODUCT_AREAS } from './useAIVisibilitySettingsForm';

const SUGGESTION_SKELETON_WIDTHS = [
  'w-28',
  'w-36',
  'w-24',
  'w-40',
  'w-32',
  'w-20',
  'w-44',
  'w-28',
  'w-36',
  'w-24',
] as const;

type SuggestProductAreasDialogProps = {
  domain: string;
  existingProductAreaCount: number;
  onCancel: () => void;
  onConfirm: (productAreas: string[]) => void;
};

const SuggestProductAreasDialog = ({
  domain,
  existingProductAreaCount,
  onCancel,
  onConfirm,
}: SuggestProductAreasDialogProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [newProductArea, setNewProductArea] = useState('');

  useEffect(() => {
    let isCurrent = true;

    const loadSuggestions = async () => {
      try {
        const nextSuggestions = await discoverProductAreas();
        if (!isCurrent) {
          return;
        }
        setSuggestions(nextSuggestions);
      } catch {
        if (!isCurrent) {
          return;
        }
        setError(t`Sorry, we were unable to suggest product areas. Please try again.`);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    loadSuggestions();

    return () => {
      isCurrent = false;
    };
  }, []);

  const canSelectMore = selected.length < MAX_PRODUCT_AREAS;

  const handlePressedChange = (name: string, nextPressed: boolean) => {
    setSelected((current) => {
      if (nextPressed) {
        if (current.includes(name) || current.length >= MAX_PRODUCT_AREAS) {
          return current;
        }
        return [...current, name];
      }

      return current.filter((area) => area !== name);
    });
  };

  const handleAdd = () => {
    const name = newProductArea.trim();
    if (!name || selected.includes(name) || !canSelectMore) {
      return;
    }

    if (!suggestions.includes(name)) {
      setSuggestions((current) => [...current, name]);
    }
    setSelected((current) => [...current, name]);
    setNewProductArea('');
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Suggest product areas</Trans>
          </DialogTitle>
          {error == null && (
            <DialogDescription>
              {isLoading ? (
                <Trans>
                  Scanning <strong>{domain}</strong>…
                </Trans>
              ) : (
                <>
                  <Trans>
                    We scanned <strong>{domain}</strong>. Pick 2 to 5 areas, and add any we missed.
                    Describe what the product does, not what it's called (e.g., "Paid Search
                    Optimization," not "Channel99 Search"). We use these to compare you against
                    competitors in the same space.
                  </Trans>
                  {existingProductAreaCount > 0 && (
                    <>
                      {' '}
                      <Trans>These product areas will replace the ones you already have.</Trans>
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          )}
        </DialogHeader>
        {isLoading ? (
          <div className="flex flex-wrap gap-2" role="status" aria-label={t`Loading...`}>
            {SUGGESTION_SKELETON_WIDTHS.map((width, index) => (
              <Skeleton key={index} className={cn('h-7 rounded-xs', width)} />
            ))}
          </div>
        ) : error != null ? (
          <FieldError errors={[{ message: error }]} />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {suggestions.map((name) => {
              const isPressed = selected.includes(name);

              return (
                <ToggleBadge
                  key={name}
                  pressed={isPressed}
                  disabled={!isPressed && !canSelectMore}
                  onPressedChange={(nextPressed) => handlePressedChange(name, nextPressed)}
                >
                  {name}
                </ToggleBadge>
              );
            })}
            {canSelectMore && (
              <ButtonGroup shape="round" className="w-56">
                <TextInput
                  aria-label={t`New product area`}
                  placeholder={t`Add product area...`}
                  size="medium"
                  value={newProductArea}
                  onChange={(event) => setNewProductArea(event.target.value)}
                  onKeyDown={submitOnEnter(handleAdd)}
                />
                <Button
                  type="button"
                  className="px-2"
                  aria-label={t`Add product area`}
                  onClick={handleAdd}
                >
                  <PlusIcon />
                </Button>
              </ButtonGroup>
            )}
          </div>
        )}
        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="large">
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <Button
            type="button"
            size="large"
            variant="on-primary"
            disabled={isLoading || selected.length < MIN_PRODUCT_AREAS}
            onClick={() => onConfirm(selected)}
          >
            <Trans>Add</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestProductAreasDialog;
